import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { downloadPdf, printPdf } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { Plus, Trash2, Printer, Download, Mail, Check, ReceiptText, X, FileSignature, FileText, Hash, CheckCircle2, Undo2 } from "lucide-react";
import { toast } from "sonner";

const cur = (region) => (region === "IE" ? "€" : "£");
const emptyLine = () => ({ description: "", qty: "", rate: "", amount: "" });

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function InvoicesPanel({ embedded }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const c = cur(user?.region);
  const [items, setItems] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [stmtOpen, setStmtOpen] = useState(false);
  const [numOpen, setNumOpen] = useState(false);
  const [numSaving, setNumSaving] = useState(false);
  const [num, setNum] = useState({ prefix: "SB", include_year: false, pad: 4, start_number: "", next_number: 1 });

  const load = async () => {
    setLoading(true);
    try {
      const [inv, drv, asg] = await Promise.all([
        api.get("/self-billing-invoices"),
        api.get("/drivers"),
        api.get("/form-assignments"),
      ]);
      setItems(inv.data);
      setAssignments(asg.data);
      setDrivers(drv.data.filter((d) => !d.leave_date || String(d.leave_date).slice(0, 10) >= new Date().toISOString().slice(0, 10)));
    } catch { toast.error("Could not load invoices"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (location.state?.prefill) {
      setPrefill(location.state.prefill);
      setOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (filter === "paid") return items.filter((i) => i.paid);
    if (filter === "unpaid") return items.filter((i) => !i.paid);
    return items;
  }, [items, filter]);

  const totals = useMemo(() => {
    const by = {};
    items.forEach((i) => {
      const sym = i.currency || c;
      const b = by[sym] || (by[sym] = { sym, unpaid: 0, paid: 0 });
      const v = Number(i.total) || 0;
      if (i.paid) b.paid += v; else b.unpaid += v;
    });
    const list = Object.values(by);
    return list.length ? list : [{ sym: c, unpaid: 0, paid: 0 }];
  }, [items, c]);

  const unbilled = useMemo(
    () => assignments.filter((a) => a.source_type === "weekly_timesheet" && a.status === "signed" && !a.invoiced_invoice_no),
    [assignments]
  );

  const fmtUK = (iso) => { try { const [y, m, dd] = String(iso).slice(0, 10).split("-"); return `${dd}/${m}/${y}`; } catch { return iso; } };
  const tsPrefill = (a) => {
    let from = "";
    if (a.week_ending) { const dt = new Date(a.week_ending); dt.setDate(dt.getDate() - 6); from = dt.toISOString().slice(0, 10); }
    const basisDay = a.rate_basis === "day";
    const label = a.week_ending ? `Driving services — week ending ${fmtUK(a.week_ending)}` : "Driving services";
    return {
      driver_id: a.driver_id, source_assignment_id: a.id,
      period_from: from, period_to: (a.week_ending || "").slice(0, 10),
      line_items: [{ description: basisDay ? `${label} (${a.qty || ""} days)`.trim() : label, qty: a.qty || "", rate: a.rate || "", amount: "" }],
    };
  };
  const openFromTimesheet = (a) => { setPrefill(tsPrefill(a)); setOpen(true); };

  const togglePaid = async (rec) => {
    try {
      const { data } = await api.post(`/self-billing-invoices/${rec.id}/toggle-paid`);
      setItems((x) => x.map((i) => (i.id === rec.id ? { ...i, paid: data.paid, paid_date: data.paid_date, paid_method: undefined, paid_reference: undefined } : i)));
      toast.success(data.paid ? `Invoice ${rec.invoice_no} marked as paid` : `Invoice ${rec.invoice_no} marked as unpaid`);
    }
    catch { toast.error("Could not update"); }
  };
  const [payFor, setPayFor] = useState(null);
  const recordPayment = async ({ paid_date, method, reference }) => {
    const rec = payFor;
    try {
      const { data } = await api.post(`/self-billing-invoices/${rec.id}/record-payment`, { paid_date, method, reference });
      setItems((x) => x.map((i) => (i.id === rec.id ? { ...i, ...data } : i)));
      setPayFor(null);
      toast.success(`Payment recorded for ${rec.invoice_no}`);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not record payment"); }
  };
  const remove = async (rec) => {
    if (!window.confirm(`Delete invoice ${rec.invoice_no}? This cannot be undone.`)) return;
    try { await api.delete(`/self-billing-invoices/${rec.id}`); setItems((x) => x.filter((i) => i.id !== rec.id)); toast.success("Invoice deleted"); }
    catch { toast.error("Could not delete"); }
  };
  const email = async (rec) => {
    const to = window.prompt("Send invoice to email:", rec.haulier_email || "");
    if (!to) return;
    try { await api.post(`/self-billing-invoices/${rec.id}/email`, { to }); toast.success(`Emailed to ${to}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send email"); }
  };

  const fmtMoney = (v, sym) => (v === undefined || v === null || v === "" ? "—" : `${sym || c} ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  const pad0 = (n, p) => String(Math.max(1, Number(n) || 1)).padStart(Math.max(1, Number(p) || 1), "0");
  const numPreview = (s) => {
    const start = s.start_number ? Number(s.start_number) : (s.next_number || 1);
    const parts = [(s.prefix || "SB").trim() || "SB"];
    if (s.include_year) parts.push(String(new Date().getFullYear()));
    parts.push(pad0(start, s.pad));
    return parts.join("-");
  };
  const openNumbering = async () => {
    try {
      const { data } = await api.get("/invoice-settings");
      setNum({ prefix: data.prefix, include_year: data.include_year, pad: data.pad, start_number: "", next_number: data.next_number });
    } catch { setNum({ prefix: "SB", include_year: false, pad: 4, start_number: "", next_number: 1 }); }
    setNumOpen(true);
  };
  const saveNumbering = async () => {
    setNumSaving(true);
    try {
      const payload = { prefix: num.prefix, include_year: num.include_year, pad: Number(num.pad) || 4 };
      if (String(num.start_number).trim() !== "") payload.start_number = Number(num.start_number);
      await api.put("/invoice-settings", payload);
      toast.success("Invoice numbering saved");
      setNumOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save numbering"); }
    setNumSaving(false);
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        {!embedded ? (
          <div>
            <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2">
              <ReceiptText size={22} /> Invoices
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Self-billing invoices you raise on behalf of drivers &amp; subcontractors.</p>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Self-billing invoices you raise on behalf of drivers &amp; subcontractors.</p>
        )}
        <div className="flex items-center gap-2">
          <Button data-testid="numbering-button" variant="outline" onClick={openNumbering} className="rounded-md gap-2 border-slate-300">
            <Hash size={16} /> Numbering
          </Button>
          <Button data-testid="statement-button" variant="outline" onClick={() => setStmtOpen(true)} className="rounded-md gap-2 border-slate-300">
            <FileText size={16} /> Monthly statement
          </Button>
          <Button data-testid="new-invoice-button" onClick={() => { setPrefill(null); setOpen(true); }} className="bg-black hover:bg-slate-800 rounded-md gap-2">
            <Plus size={16} /> New invoice
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Outstanding</p>
          <div data-testid="stat-unpaid" className="mt-1 space-y-0.5">
            {totals.map((t) => <p key={t.sym} className="text-2xl font-black text-amber-600 leading-tight">{fmtMoney(t.unpaid, t.sym)}</p>)}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Paid</p>
          <div data-testid="stat-paid" className="mt-1 space-y-0.5">
            {totals.map((t) => <p key={t.sym} className="text-2xl font-black text-emerald-600 leading-tight">{fmtMoney(t.paid, t.sym)}</p>)}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Invoices</p>
          <p className="text-2xl font-black text-slate-900 mt-1">{items.length}</p>
        </div>
      </div>

      {unbilled.length > 0 && (
        <div data-testid="unbilled-timesheets" className="mb-5 rounded-lg border border-amber-200 bg-amber-50/70 p-4">
          <p className="font-heading font-bold text-sm text-slate-900 flex items-center gap-2">
            <FileSignature size={15} className="text-amber-600" /> Signed time sheets ready to invoice
          </p>
          <p className="text-xs text-slate-500 mt-0.5">These weekly time sheets have been signed but not yet billed. Create the invoice in one click.</p>
          <ul className="mt-3 space-y-2">
            {unbilled.map((a) => (
              <li key={a.id} data-testid={`unbilled-row-${a.id}`} className="flex items-center justify-between gap-3 rounded-md bg-white border border-amber-100 px-3 py-2">
                <div className="min-w-0 text-sm">
                  <span className="font-semibold text-slate-900">{a.driver_name || "Driver"}</span>
                  <span className="text-slate-500">
                    {a.week_ending ? ` · w/e ${fmtUK(a.week_ending)}` : ""}
                    {a.qty ? ` · ${a.qty} ${a.rate_basis === "day" ? "days" : "hrs"}` : ""}
                    {a.rate ? ` @ ${c}${a.rate}` : ""}
                  </span>
                </div>
                <Button data-testid={`invoice-unbilled-${a.id}`} size="sm" className="shrink-0 gap-1.5 bg-slate-900 hover:bg-slate-800" onClick={() => openFromTimesheet(a)}>
                  <ReceiptText size={14} /> Create invoice
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        {[["all", "All"], ["unpaid", "Unpaid"], ["paid", "Paid"]].map(([k, l]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="invoices-empty">
            <ReceiptText size={32} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500 text-sm">No invoices yet. Click <b>New invoice</b> to raise your first self-billing invoice.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Invoice</th>
                <th className="text-left font-semibold px-4 py-2.5">Driver / Haulier</th>
                <th className="text-left font-semibold px-4 py-2.5">Date</th>
                <th className="text-right font-semibold px-4 py-2.5">Total</th>
                <th className="text-center font-semibold px-4 py-2.5">Status</th>
                <th className="text-right font-semibold px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((rec) => (
                <tr key={rec.id} data-testid={`invoice-row-${rec.invoice_no}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-semibold text-slate-900">{rec.invoice_no}</td>
                  <td className="px-4 py-3 text-slate-700">{rec.haulier_name}</td>
                  <td className="px-4 py-3 text-slate-500">{rec.invoice_date}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{rec.total !== undefined && rec.total !== null ? fmtMoney(rec.total, rec.currency) : <span className="text-xs font-normal text-slate-400">Blank form</span>}</td>
                  <td className="px-4 py-3 text-center">
                    <button data-testid={`toggle-paid-${rec.invoice_no}`} onClick={() => togglePaid(rec)}
                      title={rec.paid ? "Paid — click to mark unpaid" : "Click to mark as paid"}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer transition-colors ring-1 ring-inset ${rec.paid ? "bg-emerald-100 text-emerald-700 ring-emerald-200 hover:bg-emerald-200" : "bg-amber-100 text-amber-700 ring-amber-200 hover:bg-amber-200"}`}>
                      {rec.paid ? "Paid" : "Unpaid"}
                    </button>
                    {rec.paid && (rec.paid_date || rec.paid_method) && (
                      <div className="text-[10px] text-slate-400 mt-1" title={rec.paid_reference ? `Ref: ${rec.paid_reference}` : undefined}>
                        {rec.paid_date ? fmtUK(rec.paid_date) : ""}{rec.paid_method ? ` · ${rec.paid_method}` : ""}{rec.paid_reference ? ` · ${rec.paid_reference}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {rec.paid ? (
                        <button data-testid={`mark-unpaid-${rec.invoice_no}`} title="Mark as unpaid" onClick={() => togglePaid(rec)} className="p-1.5 text-slate-400 hover:text-amber-600"><Undo2 size={16} /></button>
                      ) : (
                        <button data-testid={`pay-now-${rec.invoice_no}`} title="Record payment" onClick={() => setPayFor(rec)} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 px-2.5 py-1 text-xs font-semibold"><CheckCircle2 size={15} /> Pay now</button>
                      )}
                      <button data-testid={`print-${rec.invoice_no}`} title="Print" onClick={() => printPdf(`/self-billing-invoices/${rec.id}/pdf`)} className="p-1.5 text-slate-500 hover:text-slate-900"><Printer size={16} /></button>
                      <button data-testid={`download-${rec.invoice_no}`} title="Download" onClick={() => downloadPdf(`/self-billing-invoices/${rec.id}/pdf`, `${rec.invoice_no}.pdf`)} className="p-1.5 text-slate-500 hover:text-slate-900"><Download size={16} /></button>
                      <button data-testid={`email-${rec.invoice_no}`} title="Email" onClick={() => email(rec)} className="p-1.5 text-slate-500 hover:text-slate-900"><Mail size={16} /></button>
                      <button data-testid={`delete-${rec.invoice_no}`} title="Delete" onClick={() => remove(rec)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <NewInvoiceDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPrefill(null); }} drivers={drivers} region={user?.region} prefill={prefill} onCreated={(rec, remembered) => {
        setItems((x) => [rec, ...x]);
        if (remembered && rec.driver_id && rec.currency_code) {
          setDrivers((x) => x.map((d) => (d.id === rec.driver_id ? { ...d, default_currency: rec.currency_code } : d)));
        }
        if (prefill?.source_assignment_id) {
          setAssignments((x) => x.map((a) => (a.id === prefill.source_assignment_id ? { ...a, invoiced_invoice_no: rec.invoice_no } : a)));
        }
        setOpen(false); setPrefill(null);
        downloadPdf(`/self-billing-invoices/${rec.id}/pdf`, `${rec.invoice_no}.pdf`);
        toast.success(`Invoice ${rec.invoice_no} created`);
      }} />
      <StatementDialog open={stmtOpen} onOpenChange={setStmtOpen} drivers={drivers} />
      <RecordPaymentDialog rec={payFor} onOpenChange={(o) => { if (!o) setPayFor(null); }} onSubmit={recordPayment} region={user?.region} />

      <Dialog open={numOpen} onOpenChange={setNumOpen}>
        <DialogContent data-testid="numbering-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Hash size={18} /> Invoice numbering</DialogTitle>
            <DialogDescription>Set how your invoice numbers look so they match your books. Applies to every new invoice from now on.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="rounded-lg bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wide text-slate-300 font-semibold">Next invoice</span>
              <span data-testid="numbering-preview" className="font-mono text-lg font-black tracking-wide">{numPreview(num)}</span>
            </div>
            <Field label="Prefix">
              <Input data-testid="numbering-prefix" value={num.prefix} maxLength={12}
                onChange={(e) => setNum((s) => ({ ...s, prefix: e.target.value }))} placeholder="e.g. DLZ" />
            </Field>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input data-testid="numbering-year" type="checkbox" checked={num.include_year}
                onChange={(e) => setNum((s) => ({ ...s, include_year: e.target.checked }))}
                className="w-4 h-4 accent-slate-900" />
              <span className="text-sm text-slate-700">Include the year (e.g. {(num.prefix || "SB")}-{new Date().getFullYear()}-{pad0(1, num.pad)}). Resets to 1 each January.</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Digits">
                <Select value={String(num.pad)} onValueChange={(v) => setNum((s) => ({ ...s, pad: Number(v) }))}>
                  <SelectTrigger data-testid="numbering-pad"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n} (e.g. {pad0(1, n)})</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Start at (optional)">
                <Input data-testid="numbering-start" type="number" min={1} value={num.start_number}
                  onChange={(e) => setNum((s) => ({ ...s, start_number: e.target.value }))}
                  placeholder={String(num.next_number || 1)} />
              </Field>
            </div>
            <p className="text-xs text-slate-400">Set “Start at” to continue from your existing books (e.g. if your last invoice was 150, start at 151). Leave blank to keep counting.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNumOpen(false)}>Cancel</Button>
            <Button data-testid="numbering-save" onClick={saveNumbering} disabled={numSaving} className="bg-black hover:bg-slate-800 gap-2">
              <Check size={16} /> {numSaving ? "Saving…" : "Save numbering"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecordPaymentDialog({ rec, onOpenChange, onSubmit }) {
  const today = new Date().toISOString().slice(0, 10);
  const [paidDate, setPaidDate] = useState(today);
  const [method, setMethod] = useState("Bank transfer");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (rec) { setPaidDate(today); setMethod("Bank transfer"); setReference(""); }
  }, [rec]); // eslint-disable-line

  const submit = async () => {
    setBusy(true);
    await onSubmit({ paid_date: paidDate, method, reference });
    setBusy(false);
  };

  return (
    <Dialog open={!!rec} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 size={18} className="text-emerald-600" /> Record payment</DialogTitle>
          <DialogDescription>Log the payment for invoice <b>{rec?.invoice_no}</b>. This marks it as paid and stamps the details on the invoice PDF.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <Field label="Date paid"><DatePicker testid="pay-date" value={paidDate} onChange={setPaidDate} /></Field>
          <Field label="Method">
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Bank transfer", "Cash", "Cheque", "Card", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reference (optional)">
            <Input data-testid="pay-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. bank ref / cheque no." />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="pay-confirm" onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
            <Check size={16} /> {busy ? "Saving…" : "Mark as paid"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatementDialog({ open, onOpenChange, drivers }) {
  const now = new Date();
  const months = Array.from({ length: 12 }).map((_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return { value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString(undefined, { month: "long", year: "numeric" }) };
  });
  const [driverId, setDriverId] = useState("");
  const [month, setMonth] = useState(months[0].value);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setDriverId(""); setMonth(months[0].value); } }, [open]); // eslint-disable-line

  const gen = async () => {
    if (!driverId) { toast.error("Select a driver"); return; }
    setBusy(true);
    const drv = drivers.find((d) => d.id === driverId);
    const ok = await downloadPdf(`/self-billing-statement?driver_id=${driverId}&month=${month}`, `statement-${(drv?.name || "driver").replace(/ /g, "_")}-${month}.pdf`);
    setBusy(false);
    if (ok) onOpenChange(false);
  };

  const emailIt = async () => {
    if (!driverId) { toast.error("Select a driver"); return; }
    const drv = drivers.find((d) => d.id === driverId);
    const to = window.prompt("Email statement to:", drv?.personal_email || "");
    if (!to) return;
    setBusy(true);
    try {
      const { data } = await api.post("/self-billing-statement/email", { driver_id: driverId, month, to });
      toast.success(`Statement emailed to ${data.to}`);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send statement"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="statement-dialog">
        <DialogHeader>
          <DialogTitle>Monthly statement</DialogTitle>
          <DialogDescription>A month-end PDF summary of all self-billing invoices raised for a driver, with paid and outstanding totals.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <Field label="Driver / subcontractor">
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger data-testid="stmt-driver"><SelectValue placeholder="Select driver" /></SelectTrigger>
              <SelectContent>
                {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Month">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="stmt-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {months.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="stmt-email" variant="outline" onClick={emailIt} disabled={busy} className="border-slate-300 gap-2">
            <Mail size={16} /> Email to driver
          </Button>
          <Button data-testid="stmt-generate" onClick={gen} disabled={busy} className="bg-black hover:bg-slate-800 gap-2">
            <Download size={16} /> {busy ? "Working…" : "Download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewInvoiceDialog({ open, onOpenChange, drivers, region, prefill, onCreated }) {
  const acctCur = region === "IE" ? "EUR" : "GBP";
  const [driverId, setDriverId] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [vat, setVat] = useState(false);
  const [cis, setCis] = useState(false);
  const [cisRate, setCisRate] = useState("20");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [sourceAssignmentId, setSourceAssignmentId] = useState("");
  const [currency, setCurrency] = useState(acctCur);
  const [rememberCur, setRememberCur] = useState(false);
  const [saving, setSaving] = useState(false);
  const cSym = currency === "EUR" ? "€" : "£";

  useEffect(() => {
    if (!open) return;
    if (prefill) {
      setDriverId(prefill.driver_id || "");
      setLines(prefill.line_items && prefill.line_items.length ? prefill.line_items.map((l) => ({ description: l.description || "", qty: l.qty ?? "", rate: l.rate ?? "", amount: l.amount ?? "" })) : [emptyLine()]);
      setPeriodFrom(prefill.period_from || "");
      setPeriodTo(prefill.period_to || "");
      setSourceAssignmentId(prefill.source_assignment_id || "");
      const d = drivers.find((x) => x.id === prefill.driver_id);
      setVat(!!(d && (d.vat_registered || d.company_vat_number)));
      setCurrency((d && d.default_currency) || acctCur);
      setRememberCur(false);
      setCis(false); setCisRate("20");
    } else {
      setDriverId(""); setLines([emptyLine()]); setVat(false); setCis(false); setCisRate("20");
      setPeriodFrom(""); setPeriodTo(""); setSourceAssignmentId(""); setCurrency(acctCur); setRememberCur(false);
    }
  }, [open]); // eslint-disable-line

  const onPickDriver = (id) => {
    setDriverId(id);
    const d = drivers.find((x) => x.id === id);
    setVat(!!(d && (d.vat_registered || d.company_vat_number)));
    if (d && d.default_currency) setCurrency(d.default_currency);
  };

  const lineAmount = (l) => {
    if (l.amount !== "") return Number(l.amount) || 0;
    if (l.qty !== "" && l.rate !== "") return (Number(l.qty) || 0) * (Number(l.rate) || 0);
    return 0;
  };
  const vatRate = currency === "EUR" ? 0.23 : 0.20;
  const vatPct = currency === "EUR" ? 23 : 20;
  const subtotal = lines.reduce((s, l) => s + lineAmount(l), 0);
  const vatAmt = vat ? subtotal * vatRate : 0;
  const cisAmt = cis ? subtotal * ((Number(cisRate) || 0) / 100) : 0;
  const total = subtotal + vatAmt - cisAmt;
  const money = (v) => `${cSym} ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const setLine = (i, patch) => setLines((x) => x.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const submit = async () => {
    if (!driverId) { toast.error("Select a driver"); return; }
    const valid = lines.filter((l) => l.description.trim() && lineAmount(l) > 0);
    if (valid.length === 0) { toast.error("Add at least one line with a description and amount"); return; }
    setSaving(true);
    try {
      const payload = {
        driver_id: driverId,
        line_items: valid.map((l) => ({
          description: l.description.trim(),
          qty: l.qty === "" ? null : Number(l.qty),
          rate: l.rate === "" ? null : Number(l.rate),
          amount: l.amount === "" ? null : Number(l.amount),
        })),
        vat, cis, cis_rate: Number(cisRate) || 20,
        period_from: periodFrom, period_to: periodTo,
        source_assignment_id: sourceAssignmentId || "",
        currency, set_driver_default: rememberCur,
      };
      const { data } = await api.post("/self-billing-invoices", payload);
      onCreated(data, rememberCur);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create invoice"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-invoice-dialog">
        <DialogHeader>
          <DialogTitle>New self-billing invoice</DialogTitle>
          <DialogDescription>Raise a self-billing invoice on behalf of a driver. Add the work as line items — the totals calculate automatically.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <Field label="Driver / subcontractor">
                <Select value={driverId} onValueChange={onPickDriver}>
                  <SelectTrigger data-testid="inv-driver"><SelectValue placeholder="Select driver" /></SelectTrigger>
                  <SelectContent>
                    {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.company_name && (d.engagement_type || "").startsWith("Subcontractor") ? `${d.company_name} (${d.name})` : d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Period from"><DatePicker testid="inv-period-from" value={periodFrom} onChange={setPeriodFrom} /></Field>
            <Field label="Period to"><DatePicker testid="inv-period-to" value={periodTo} onChange={setPeriodTo} /></Field>
          </div>

          <div className="flex flex-wrap items-center gap-4 border-t border-slate-100 pt-3">
            <div>
              <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Currency</span>
              <div className="mt-1 inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="inv-currency">
                {[["GBP", "£ GBP"], ["EUR", "€ EUR"]].map(([code, label]) => (
                  <button key={code} type="button" data-testid={`inv-currency-${code}`} onClick={() => setCurrency(code)}
                    className={`px-3 py-1.5 text-sm font-semibold transition-colors ${currency === code ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {driverId && (
              <label data-testid="inv-remember-currency" className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 mt-4">
                <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-slate-900" checked={rememberCur} onChange={(e) => setRememberCur(e.target.checked)} />
                Remember {cSym} as the default for this driver
              </label>
            )}
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Line items</p>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} data-testid={`inv-line-${i}`} className="grid grid-cols-12 gap-2 items-center">
                  <Input data-testid={`inv-line-desc-${i}`} className="col-span-5" placeholder="Description of work" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} />
                  <Input data-testid={`inv-line-qty-${i}`} className="col-span-2" type="number" step="0.25" placeholder="Qty/hrs" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value, amount: "" })} />
                  <Input data-testid={`inv-line-rate-${i}`} className="col-span-2" type="number" step="0.01" placeholder="Rate" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value, amount: "" })} />
                  <Input data-testid={`inv-line-amount-${i}`} className="col-span-2" type="number" step="0.01" placeholder="Amount" value={l.amount !== "" ? l.amount : (l.qty !== "" && l.rate !== "" ? (Number(l.qty) * Number(l.rate)).toFixed(2) : "")} onChange={(e) => setLine(i, { amount: e.target.value, qty: "", rate: "" })} />
                  <button className="col-span-1 flex justify-center text-slate-400 hover:text-red-600" onClick={() => setLines((x) => x.length > 1 ? x.filter((_, idx) => idx !== i) : x)}><X size={16} /></button>
                </div>
              ))}
            </div>
            <button data-testid="inv-add-line" onClick={() => setLines((x) => [...x, emptyLine()])} className="mt-2 text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
              <Plus size={14} /> Add line
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-100 pt-3">
            <label data-testid="inv-vat" className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-slate-900" checked={vat} onChange={(e) => setVat(e.target.checked)} />
              Add VAT @ {vatPct}%
            </label>
            <label data-testid="inv-cis" className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-slate-900" checked={cis} onChange={(e) => setCis(e.target.checked)} />
              {region === "IE" ? "Deduct RCT" : "Deduct CIS"}
            </label>
            {cis && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Rate %</span>
                <Input data-testid="inv-cis-rate" className="w-20 h-8" type="number" value={cisRate} onChange={(e) => setCisRate(e.target.value)} />
              </div>
            )}
            <p className="text-xs text-slate-400">Most drivers are no VAT / no CIS — leave both unticked.</p>
          </div>

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm">
            <div className="flex justify-between py-0.5"><span className="text-slate-500">Subtotal</span><span data-testid="inv-subtotal" className="font-semibold text-slate-900">{money(subtotal)}</span></div>
            {vat && <div className="flex justify-between py-0.5"><span className="text-slate-500">VAT @ {vatPct}%</span><span className="font-semibold text-slate-900">{money(vatAmt)}</span></div>}
            {cis && <div className="flex justify-between py-0.5"><span className="text-slate-500">Less {region === "IE" ? "RCT" : "CIS"} ({cisRate}%)</span><span className="font-semibold text-red-600">-{money(cisAmt)}</span></div>}
            <div className="flex justify-between pt-2 mt-1 border-t border-slate-200"><span className="font-bold text-slate-900">Net payable</span><span data-testid="inv-total" className="font-black text-slate-900">{money(total)}</span></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="inv-create" onClick={submit} disabled={saving} className="bg-black hover:bg-slate-800 gap-2">
            <Check size={16} /> {saving ? "Creating…" : "Create invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Invoices() {
  return <InvoicesPanel />;
}
